import axios from "axios";
import * as Sentry from "@sentry/react";

import type { AppDispatch } from "../state/rootStore";
import type { AiSettingsForm } from "../../Pages/Projects/Tabs/AiSettingsTab/AiSettingsTab";

import { notifyError, notifySuccess } from "./notify";
import {
  NEW_ORG_LOGO,
  ONE_ORGANIZATION_LOADED,
  ONE_ORGANIZATION_UPDATED,
  ORGANIZATIONS_LOADED,
  ORGANIZATIONS_LOADING,
  ORGANIZATION_RESET_USERS,
  UPDATE_ORG_LOGO,
} from "./types";

export const getAllOrganizationsAdmin =
  () =>
  async (dispatch: AppDispatch, getState): Promise<void> => {
    dispatch({ type: ORGANIZATIONS_LOADING });
    const userRole = getState()?.auth?.role;

    if (!userRole || userRole === "user" || userRole === "guest") return;

    try {
      const res = await axios.get("/organization/admin/all");

      dispatch({ type: ORGANIZATIONS_LOADED, payload: res.data.organizations });
    } catch (error) {
      console.error(error);
    }
  };

export const getAllOrganizations =
  () =>
  async (dispatch: AppDispatch, getState): Promise<void> => {
    dispatch({ type: ORGANIZATIONS_LOADING });
    const userRole = getState()?.auth?.role;
    const currLogo = getState()?.organizations?.organizationLogo;
    const newLogo = getState()?.organizations?.newLogo;

    if (!userRole || userRole === "user" || userRole === "guest") return;

    try {
      const res = await axios.get("/organization");

      dispatch({ type: ORGANIZATIONS_LOADED, payload: res.data.organizations });

      if ((res.data.organizations[0]?.logoUrl && currLogo === "") || newLogo) {
        dispatch({ type: UPDATE_ORG_LOGO, payload: { logoUrl: res.data.organizations[0]?.logoUrl } });
        dispatch({ type: NEW_ORG_LOGO, payload: { newLogo: false } });
      }
    } catch (error) {
      console.error(error);
    }
  };

// action to create a new group
export const createOrganization = () => async (dispatch, getState) => {
  await axios.post("/organization/create", { ...getState().form.organization.values });
  dispatch(getAllOrganizations());
};

export const editAiSettings = (values: AiSettingsForm) => async (dispatch: AppDispatch) => {
  try {
    const organizationCode = values.organizationCode;
    const service = values.isCustomServicesActive ? values.service : "";
    const hideFillerWords = values.isHideFillerWords;
    const customVocabulary = values.customVocabulary.split(",").map((item) => item.trim());
    const createChaptersAuto = values.isCreateChaptersAuto;
    const customPromptChapter = values.customPromptChapter;
    const customPromptSummary = values.customPromptSummary;

    const settings = {
      transcription_service: service,
      filler_words: hideFillerWords,
      vocabulary: customVocabulary,
      auto_chaptering: createChaptersAuto,
      summary_prompt: customPromptSummary ?? "",
      chaptering_prompt: customPromptChapter ?? "",
    };

    await axios.put(`/organization/${organizationCode}/edit`, { settings });

    dispatch(notifySuccess(`Settings have been edited successfully`));

    dispatch(getAllOrganizations());
  } catch (error) {
    dispatch(notifyError(`Error editing organization settings`));
    Sentry.withScope((scope) => {
      scope.setTag("values", JSON.stringify(values));
      scope.setTag("method", "edit-ai-settings");
      Sentry.captureException(error);
    });
  }
};

export const editOrganization = (organizationalCode, values) => async (dispatch: AppDispatch, getState) => {
  const organization = getState().organizations.organizations[organizationalCode];
  try {
    const res = await axios.put(`/organization/${organizationalCode}/edit`, values);
    dispatch(notifySuccess(`The Organization have been edited successfully`));
    const payload = { organization: res.data };

    if (res.data.require_session_passwords && !organization.require_session_passwords) {
      const res2 = await axios.post("/prepsession/all", {
        count: true,
        org_id: res.data._id,
        password_present: false,
      });
      payload.organization.unsecure_prepsession_count = res2.data.prepsession_count;
    }
    dispatch({ type: ONE_ORGANIZATION_LOADED, payload });
    dispatch(getAllOrganizations());
  } catch (error) {
    dispatch(notifyError(`Error adding editing organization`));
  }
};

export const setPasswordOnUnsecurePrepSessions = (organizationalCode, password) => async (dispatch) => {
  await axios.post(`/organization/${organizationalCode}/lock_unsecure_prep_sessions`, { password });
  dispatch({
    type: ONE_ORGANIZATION_UPDATED,
    payload: {
      organizationalCode,
      values: { unsecure_prepsession_count: 0 },
    },
  });
};

export const addUsersOfOrganizationToDefaultGroups = (organization) => async (dispatch) => {
  const res = await axios.post("/organization/add_users_to_group", { organization });
  console.log(res);
  dispatch(notifySuccess("Successfully added existing organizational users to default groups"));
};

export const getOrganization = (code) => async (dispatch) => {
  dispatch({ type: ORGANIZATIONS_LOADING });
  const res = await axios.get(`/organization/${code}`);
  dispatch({ type: ONE_ORGANIZATION_LOADED, payload: res.data });
};

export const resetOrganizationUsers = () => async (dispatch) => {
  dispatch({ type: ORGANIZATION_RESET_USERS });
};
